/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { DirectionalHint, IContextualMenuItemProps } from '@fluentui/react/lib/ContextualMenu';
import { ITheme } from '@fluentui/react/lib/Styling';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { CustomizerContext, IRenderFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { showTemplateManagementCoachButtonMark } from '../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { dataExportButtonStatus, dataExportModalProps } from '../../../../apollo/reactiveVariables/UIVariables';
import { ListData } from '../../../../data/types.generated';
import { CanExportClient, ExportState } from '../../../../service/gen/export';
import { useApprovalsQuery } from '../../../../utilities/ApolloRestUtils';
import { getAppHost } from '../../../../utilities/TeamsUtils';
import { CoachMark, useCoachMarkState } from '../../../../utilities/TemplateUtils';
import { getTeachingBubbleStyles } from '../../../components/ApprovalsAppHeader/ApprovalsAppHeader.styles';
import { DataExportModalType } from './DataExportModal';
import './DataExportModal.scss';
import { getExportingStyles, getNoDataStyles } from './DataExportModal.styles';

// This hook will return the current Data Export state.
// Internally it will poll an backend api /CanExport to check current user data export task status.
export const useDataExportButton = (listData: ListData): ICommandBarItemProps => {
    const loc = getAppHost().getLocale();
    const themeName = getAppHost().getTheme();
    const dataExportEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.DataExportEnabled;
    const dataExportPollInterval = getAppHost().getEcsConfig().MicrosoftTeamApprovals.DataExportStatusPollInterval;
    const DataExportCoachMarkEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.DataExportCoachMarkEnabled;

    const theme: ITheme = React.useContext(CustomizerContext).customizations.settings.theme;
    const dataExportStatus = useReactiveVar(dataExportButtonStatus);
    const { data: approvalsData } = useApprovalsQuery(
        {
            ...listData,
            extendedParameters: null,
        },
        'cache-only'
    );
    const [showDataExportCoachMark, hideDataExportCoachMark] = useCoachMarkState(CoachMark.DataExport);
    const showTemplateManagementCoachMark = useReactiveVar(showTemplateManagementCoachButtonMark);

    // React.useEffect(() => console.debug('>>', showTemplateManagementCoachMark), [showTemplateManagementCoachMark]);
    // eslint-disable-next-line @typescript-eslint/tslint/config
    const renderCoachMark: IRenderFunction<IContextualMenuItemProps> = (props, defaultRenders) => {
        // NOTE There is a special UX spec here: if we are showing the Template Management coach mark, we will not
        // display the data export coachmark. We go this way to avoid multiple coach mark show up at the same time
        // which may confuse the user.
        return (
            <>
                <span id='data-export-button'>{defaultRenders(props)}</span>
                {!showTemplateManagementCoachMark && showDataExportCoachMark && (
                    <TeachingBubble
                        calloutProps={{
                            calloutWidth: 280,
                            directionalHint: DirectionalHint.bottomCenter,
                        }}
                        target='#data-export-button'
                        styles={getTeachingBubbleStyles}
                        onDismiss={hideDataExportCoachMark}
                        secondaryButtonProps={{
                            children: loc.strings.got_it_button_text,
                            onClick: hideDataExportCoachMark,
                        }}
                        headline={loc.strings.data_export_coachmark_title}>
                        {loc.strings.data_export_coachmark_content}
                    </TeachingBubble>
                )}
            </>
        );
    };
    React.useEffect((): (() => void) => {
        if (dataExportEnabled) {
            const pollDataExportButtonStatus = (): void => {
                new CanExportClient()
                    .canExport()
                    .then((res: ExportState): void => {
                        dataExportButtonStatus(res);
                        if (res === ExportState.Running) {
                            startPoll();
                        } else {
                            stopPoll();
                        }
                    })
                    .catch((): void => {
                        dataExportButtonStatus(null);
                    });
            };
            let timer: any;
            const startPoll = (): void => {
                if (timer != null) {
                    return;
                }

                timer = setInterval(pollDataExportButtonStatus, dataExportPollInterval);
            };

            const stopPoll = (): void => {
                clearInterval(timer);
                timer = null;
            };
            if (dataExportStatus === ExportState.Running) {
                startPoll();
            } else if (dataExportStatus === null) {
                pollDataExportButtonStatus();
            }
            return stopPoll;
        }
    }, [dataExportEnabled, dataExportPollInterval, dataExportStatus]);

    // const noApprovalsData = !(approvalsData?.ListApprovals.value?.length > 0);
    // If ListApprovalsData return empty result with current userRole/flowEnv, we set the export button to disable state.
    // Before ListApprovalData result returned, we set isApprovalsDataNotEmpty to null and don't display the export button.
    let isApprovalsDataNotEmpty = null;
    if (!!approvalsData?.ListApprovals.value) {
        isApprovalsDataNotEmpty = approvalsData.ListApprovals.value.length > 0;
    }

    if (!dataExportEnabled || isApprovalsDataNotEmpty === null) {
        return null;
    } else if (isApprovalsDataNotEmpty === false) {
        return {
            key: 'exportButton',
            text: loc.strings.data_export_command_bar_export_button_text,
            ariaLabel: loc.strings.data_export_command_bar_export_button_text,
            iconProps: { iconName: 'Export' },
            disabled: true,
            buttonStyles: getNoDataStyles(theme),
        };
    }
    // When DataExportStatus===OneDriveNotReady we will still render the export button.
    // We will alert user with detail error message when they click the button. This is by design.
    else if (dataExportStatus === ExportState.Ready || dataExportStatus === ExportState.OneDriveNotReady) {
        return {
            key: 'exportButton',
            text: loc.strings.data_export_command_bar_export_button_text,
            ariaLabel: loc.strings.data_export_command_bar_export_button_text,
            iconProps: { iconName: 'Export' },
            onClick: (): void => {
                dataExportModalProps({
                    isOpen: true,
                    modalType: DataExportModalType.Creation,
                    modalProps: null,
                });
            },
            onRenderIcon: DataExportCoachMarkEnabled ? renderCoachMark : undefined,
        };
    } else if (dataExportStatus === ExportState.Running) {
        return {
            key: 'exportButton',
            text: loc.strings.data_export_command_bar_export_button_text_exporting,
            ariaLabel: loc.strings.data_export_command_bar_export_button_text_exporting,
            iconProps: { iconName: 'Sync', className: 'approvals-rotating-icon' },
            buttonStyles: getExportingStyles(theme, themeName),
        };
    } else {
        return null;
    }
};
